import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { CONTACT, PROFILE, RDV, REALISATIONS } from "../config/routes";
import "./styles/Contact.css";

import contactHeroPng from "../images/png/background/contact_header_background.png";
import contactHeroAvif from "../images/avif/background/contact_header_background.avif";
import contactHeroWebp from "../images/webp/background/contact_header_background.webp";
import emailIcon from "../images/icons/email.svg";
import localisationIcon from "../images/icons/localisation.svg";
import linkedinIcon from "../images/icons/linkedin.svg";

const CONTACT_EMAIL = "floriandc.dev@gmail.com";
const LINKEDIN_URL = "https://www.linkedin.com/in/florian-da-cunha-a242a21a0/";
const GITHUB_URL = "https://github.com/floriandcdev";

const requestTypeOptions = [
  { value: "project", label: "Projet client" },
  { value: "recruitment", label: "Recrutement / CDI" },
  { value: "general", label: "Question générale" },
  { value: "other", label: "Autre" },
];

const projectExamples = [
  "MVP",
  "outil métier",
  "interface admin",
  "paiement",
  "API",
  "mobile",
  "SEO / site vitrine sérieux",
];

const trustItems = [
  "Réponse claire, sans engagement immédiat.",
  "Cadrage du besoin avant de parler solution.",
  "Informations partagées traitées avec confidentialité.",
  "Possibilité d’échanger simplement avant tout projet.",
];

function getRequestTypeLabel(value) {
  return requestTypeOptions.find((option) => option.value === value)?.label || "Autre";
}

export default function ContactV7() {
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 599 : false
  );

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [requestType, setRequestType] = useState("project");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");

  const siteOrigin = "https://dreameragency.fr";
  const canonicalUrl = `${siteOrigin}${CONTACT}`;
  const metaTitle = "Contact - Dreamer Agency / Florian Da Cunha";
  const metaDesc =
    "Contactez Florian Da Cunha pour un projet web, SaaS, mobile, dashboard, automatisation API ou une opportunité développeur full-stack React / Node.js / TypeScript.";
  const ogImage = `${siteOrigin}/media/OG/contact_header_background.png`;

  const directContacts = useMemo(
    () => [
      {
        icon: emailIcon,
        title: "Email",
        value: CONTACT_EMAIL,
        hint: "Pour un projet, une question ou une prise de contact.",
        href: `mailto:${CONTACT_EMAIL}`,
      },
      {
        icon: linkedinIcon,
        title: "LinkedIn",
        value: "Florian Da Cunha",
        hint: "Utile pour les échanges recruteur et opportunités CDI.",
        href: LINKEDIN_URL,
      },
      {
        mark: "GH",
        title: "GitHub",
        value: "floriandcdev",
        hint: "Aperçu technique complémentaire.",
        href: GITHUB_URL,
      },
      {
        icon: localisationIcon,
        title: "Localisation",
        value: "Toulouse - Paris / hybride",
        hint: "Échanges possibles à distance selon le contexte.",
        href: null,
      },
    ],
    []
  );

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact Dreamer Agency / Florian Da Cunha",
      url: canonicalUrl,
      description: metaDesc,
      about: {
        "@type": "Person",
        name: "Florian Da Cunha",
        jobTitle: "Développeur full-stack React / Node.js / TypeScript",
        email: `mailto:${CONTACT_EMAIL}`,
        sameAs: [LINKEDIN_URL, GITHUB_URL],
      },
    }),
    [canonicalUrl, metaDesc]
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();

    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }

    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".contact2026");
    if (!mainContainer) return undefined;

    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }

    return () => {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    };
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  const validate = () => {
    setErrorMsg("");
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!fullName.trim() || !email.trim() || !message.trim()) {
      setErrorMsg("Nom, email et message sont requis.");
      return false;
    }

    if (!emailRegex.test(email.trim())) {
      setErrorMsg("Adresse email invalide.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (website) return;
    if (!validate()) return;

    setSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        setErrorMsg("REACT_APP_API_URL manquant. Ajoutez-le pour activer l’envoi.");
        setSubmitting(false);
        return;
      }

      const requestTypeLabel = getRequestTypeLabel(requestType);
      const body = [
        `Type de demande: ${requestTypeLabel}`,
        `Nom: ${fullName.trim()}`,
        `Email: ${email.trim()}`,
        "",
        message.trim(),
      ].join("\n");

      const response = await axios.post(`${apiUrl}/send-email`, {
        name: fullName.trim(),
        firstName: "-",
        email: email.trim(),
        phone: "-",
        message: body,
        contactOption: "contact2026",
      });

      if (response?.data?.success) {
        setSuccess(true);
      } else {
        setErrorMsg("Impossible d’envoyer le message. Réessayez ou réservez un appel.");
      }
    } catch {
      setErrorMsg("Erreur lors de l’envoi. Réessayez ou réservez un appel.");
    } finally {
      setSubmitting(false);
    }
  };

  const helmet = (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={ogImage} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );

  if (success) {
    return (
      <main className={`contact2026 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
        {helmet}
        <section className="contact2026-successSection">
          <div className="contact2026-container">
            <div className="contact2026-success">
              <p className="contact2026-eyebrow">Message envoyé</p>
              <h1 className="contact2026-h1">Merci, votre message a bien été transmis.</h1>
              <p className="contact2026-muted">
                Je reviens vers vous avec une réponse claire. Vous pouvez aussi réserver un appel si vous préférez
                planifier un échange.
              </p>
              <div className="contact2026-successActions">
                <Link to={RDV} className="btn btn-primary">
                  Réserver un appel
                </Link>
                <Link to={REALISATIONS} className="btn btn-secondary">
                  Voir les réalisations
                </Link>
                <Link to={PROFILE} className="btn btn-ghost">
                  Profil développeur
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className={`contact2026 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
      {helmet}

      <section className="contact2026-hero">
        <picture className="contact2026-heroMedia">
          <source srcSet={contactHeroAvif} type="image/avif" />
          <source srcSet={contactHeroWebp} type="image/webp" />
          <img src={contactHeroPng} alt="" />
        </picture>
        <div className="contact2026-heroOverlay" />

        <div className="contact2026-container contact2026-heroInner">
          <p className="contact2026-eyebrow">Contact</p>
          <h1 className="contact2026-h1">Discutons de votre projet ou d’une opportunité</h1>
          <p className="contact2026-lead">
            Vous pouvez me contacter pour un projet web, SaaS, mobile ou outil métier, ou pour échanger autour
            d’un poste développeur full-stack React / Node.js / TypeScript.
          </p>
          <div className="contact2026-heroActions">
            <Link to={REALISATIONS} className="btn btn-secondary">
              Voir les réalisations
            </Link>
            <Link to={PROFILE} className="btn btn-ghost">
              Voir le profil développeur
            </Link>
          </div>
        </div>
      </section>

      <section className="contact2026-entrySection">
        <div className="contact2026-container contact2026-entryGrid">
          <article className="contact2026-pathCard">
            <p className="contact2026-eyebrow">Projet client</p>
            <h2>Vous avez un projet ?</h2>
            <p>
              Application web, SaaS, dashboard, application mobile, automatisation ou intégration API.
            </p>
            <ul className="contact2026-tagList">
              {projectExamples.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <a href="#contact-form" className="contact2026-textLink">
              Décrire le besoin
            </a>
          </article>

          <article className="contact2026-pathCard">
            <p className="contact2026-eyebrow">Recrutement</p>
            <h2>Vous recrutez un développeur full-stack ?</h2>
            <p>
              Profil React / Node.js / TypeScript, Bac+5, expérience ERP et projets freelance web/mobile/SaaS.
            </p>
            <div className="contact2026-cardActions">
              <Link to={PROFILE} className="btn btn-secondary">
                Voir le profil
              </Link>
              <a href="#contact-form" className="contact2026-textLink">
                Me contacter
              </a>
            </div>
          </article>
        </div>
      </section>

      <section className="contact2026-mainSection" id="contact-form">
        <div className="contact2026-container contact2026-contactGrid">
          <div className="formCard">
            <p className="formTitle">Envoyer un message</p>
            <p className="contact2026-muted">
              Le formulaire permet simplement de qualifier la demande. Le type choisi est ajouté au message envoyé.
            </p>

            <form className="contact2026-form" onSubmit={handleSubmit} noValidate>
              <label className="hp" htmlFor="contact-website">
                Site web
              </label>
              <input
                id="contact-website"
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                autoComplete="off"
                className="hp"
                tabIndex={-1}
              />

              <div className="row2">
                <div className="field">
                  <label htmlFor="contact-name">Nom *</label>
                  <input
                    id="contact-name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="contact-email">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="contact-request-type">Type de demande</label>
                <select
                  id="contact-request-type"
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                >
                  {requestTypeOptions.map((option) => (
                    <option value={option.value} key={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="contact-message">Message *</label>
                <textarea
                  id="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={7}
                  required
                  aria-describedby={errorMsg ? "contact-error" : undefined}
                />
              </div>

              {errorMsg ? (
                <div className="errorBox" id="contact-error" role="alert">
                  {errorMsg}
                </div>
              ) : null}

              <div className="submitRow">
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  {submitting ? "Envoi en cours..." : "Envoyer le message"}
                </button>
                <span className="contact2026-muted">
                  Le rendez-vous reste optionnel : <Link to={RDV} className="link">réserver un appel</Link>.
                </span>
              </div>
            </form>
          </div>

          <aside className="contact2026-sideStack" aria-label="Moyens de contact">
            <div className="sideCard">
              <p className="sideTitle">Contact direct</p>
              <div className="contact2026-directList">
                {directContacts.map((contact) => {
                  const content = (
                    <>
                      <div className="infoIcon">
                        {contact.icon ? (
                          <img src={contact.icon} alt="" aria-hidden="true" />
                        ) : (
                          <span>{contact.mark}</span>
                        )}
                      </div>
                      <div className="infoBody">
                        <div className="infoTitle">{contact.title}</div>
                        <div className="infoValue">{contact.value}</div>
                        <div className="infoHint">{contact.hint}</div>
                      </div>
                    </>
                  );

                  return contact.href ? (
                    <a
                      href={contact.href}
                      target={contact.href.startsWith("http") ? "_blank" : undefined}
                      rel={contact.href.startsWith("http") ? "noreferrer" : undefined}
                      className="infoCard contact2026-directCard"
                      key={contact.title}
                    >
                      {content}
                    </a>
                  ) : (
                    <div className="infoCard contact2026-directCard" key={contact.title}>
                      {content}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sideCard">
              <p className="sideTitle">Vous préférez planifier un échange ?</p>
              <p className="contact2026-muted">
                Vous pouvez réserver un appel, sans que ce soit le seul moyen de contact.
              </p>
              <Link to={RDV} className="btn btn-secondary btn-block">
                Réserver un appel
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="contact2026-reassurance">
        <div className="contact2026-container">
          <div className="contact2026-sectionIntro">
            <p className="contact2026-eyebrow">Réassurance</p>
            <h2 className="contact2026-h2">Un premier échange simple et cadré</h2>
          </div>
          <div className="contact2026-reassuranceGrid">
            {trustItems.map((item) => (
              <article className="contact2026-reassuranceCard" key={item}>
                <p>{item}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="contact2026-cta">
        <div className="contact2026-container contact2026-ctaInner">
          <div>
            <p className="contact2026-eyebrow">Avant de me contacter</p>
            <h2 className="contact2026-h2">Vous voulez vérifier le niveau de preuve ?</h2>
            <p className="contact2026-muted">
              Les réalisations et le profil développeur donnent le contexte technique avant un échange.
            </p>
          </div>
          <div className="ctaActions">
            <Link to={REALISATIONS} className="btn btn-secondary">
              Voir les réalisations
            </Link>
            <Link to={PROFILE} className="btn btn-ghost">
              Voir le profil développeur
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
